import type { CrmNoteType } from '@plato/data/prisma';
import type { ReactNode } from 'react';
import type { Control, FieldErrors } from 'react-hook-form';
import { Controller } from 'react-hook-form';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { getTextContent } from '../../../lib/rich-text/get-text-content.ts';
import type { CrmNoteInput } from '../../../server/dto/crm-note-input-dto.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import type { RichTextInputVariant } from '../../rich-text/components/rich-text-input.tsx';
import { RichTextInput } from '../../rich-text/components/rich-text-input.tsx';

export type NoteFormFieldsProps<T extends CrmNoteInput> = {
    noteType: CrmNoteType;
    control: Control<T>;
    errors: FieldErrors<T>;
    placeholderOverride?: string;
    contentClassName?: string;
    className?: string;
    variant?: RichTextInputVariant;
    disableVoiceRecording?: boolean;
};

export const NoteFormFields = <T extends CrmNoteInput>({
    noteType,
    control,
    errors,
    placeholderOverride,
    contentClassName,
    className,
    variant,
    disableVoiceRecording,
}: NoteFormFieldsProps<T>): ReactNode => {
    const { t } = useLocale();

    const getPlaceholderByType = (noteType: CrmNoteType): string => {
        switch (noteType) {
            case 'VISIT':
                return t('notes__placeholder__event', { ns: 'notes' });
            case 'PHONE_CALL':
                return t('notes__placeholder__phone_call', { ns: 'notes' });
            default:
                return t('notes__placeholder__note', { ns: 'notes' });
        }
    };

    return (
        <DialogForm.ItemList>
            {/* Title/Description Field */}
            <DialogForm.Item>
                <DialogForm.Body className="box-border py-0">
                    <Controller
                        name="title"
                        control={control as Control<CrmNoteInput>}
                        rules={{
                            validate: (value) => {
                                if (noteType !== 'NOTE') {
                                    return true;
                                }

                                const textContent = getTextContent(value);
                                return !!textContent || t('form__text_required', { ns: 'common' });
                            },
                        }}
                        render={({ field }) => (
                            <RichTextInput
                                placeholder={placeholderOverride ?? getPlaceholderByType(noteType)}
                                error={!!errors.title}
                                {...field}
                                contentClassName={contentClassName}
                                disableVoiceRecording={disableVoiceRecording}
                                className={className}
                                variant={variant}
                            />
                        )}
                    />
                </DialogForm.Body>
                <DialogForm.Error error={errors.title} />
            </DialogForm.Item>
        </DialogForm.ItemList>
    );
};
